// ─── Strategy Signal Engine ───
// Evaluates strategy entry/exit rules against candle data + live price

import { SMA, HMA, TMA, BollingerBands, type CandleData } from './indicators';
import type { EntryRule, ExitRule, Indicator } from '@/types/strategy';

export type SignalDirection = 'BUY' | 'SELL' | 'NEUTRAL';

export interface IndicatorSnapshot {
  label: string;
  currentValue: number | null;
  previousValue: number | null;
  distancePips: number | null;
  isActive: boolean;
}

export interface RuleEvaluation {
  ruleId: string;
  description: string;
  satisfied: boolean;
  reason: string;
  side?: 'buy' | 'sell';
}

export interface SignalResult {
  direction: SignalDirection;
  strength: number;
  entryReasons: string[];
  exitReasons: string[];
  indicators: IndicatorSnapshot[];
  currentPrice: number;
  pipSize: number;
  timestamp: number;
  tpLevel: number;
  slLevel: number;
}

// ─── Helpers ───────────────────────────────────────────────────

function getIndicatorArray(
  candles: CandleData[],
  ind: Indicator,
): {
    values: (number | null)[];
    upper?: (number | null)[];
    lower?: (number | null)[];
    middle?: (number | null)[];
  } {
  const closes = candles.map((c) => c.close);
  switch (ind.type) {
    case 'SMA':
      return { values: SMA(closes, ind.period) };
    case 'HMA':
      return { values: HMA(closes, ind.period) };
    case 'TMA':
      return { values: TMA(closes, ind.period) };
    case 'BOLLINGER': {
      const bb = BollingerBands(closes, ind.period, ind.deviation || 2);
      return { values: bb.middle, upper: bb.upper, lower: bb.lower, middle: bb.middle };
    }
  }
}

function lastValidValue(arr: (number | null)[]): number | null {
  for (let i = arr.length - 1; i >= 0; i--) {
    if (arr[i] !== null) return arr[i];
  }
  return null;
}

function prevValidValue(arr: (number | null)[]): number | null {
  let foundOne = false;
  for (let i = arr.length - 1; i >= 0; i--) {
    if (arr[i] !== null) {
      if (foundOne) return arr[i];
      foundOne = true;
    }
  }
  return null;
}

function detectCross(
  prices: number[],
  indValues: (number | null)[],
  condition: 'crosses_above' | 'crosses_below',
): boolean {
  if (prices.length < 2 || indValues.length < 2) return false;
  const currPrice = prices[prices.length - 1];
  const prevPrice = prices[prices.length - 2];
  const currInd = lastValidValue(indValues);
  const prevInd = prevValidValue(indValues);

  if (currInd === null || prevInd === null) return false;

  if (condition === 'crosses_above') {
    return prevPrice < prevInd && currPrice > currInd;
  }
  return prevPrice > prevInd && currPrice < currInd;
}

// ─── Rule Evaluation ────────────────────────────────────────────

function evaluateSingleRule(
  rule: EntryRule | ExitRule,
  candles: CandleData[],
  indicators: Indicator[],
  currentPrice: number,
  pipSize: number,
): { satisfied: boolean; reason: string } {
  const ind = indicators.find((i) => i.id === rule.indicatorId);
  if (!ind) return { satisfied: false, reason: 'Indicator not found' };

  const computed = getIndicatorArray(candles, ind);
  const values = computed.values;
  const lastVal = lastValidValue(values);
  const prevVal = prevValidValue(values);
  const prices = candles.map((c) => c.close);

  const indLabel = `${ind.type}(${ind.period}${ind.type === 'BOLLINGER' ? `,${ind.deviation}` : ''})`;

  switch (rule.condition) {
    case 'crosses_above': {
      const crossed = detectCross(prices, values, 'crosses_above');
      return {
        satisfied: crossed,
        reason: crossed
          ? `${indLabel} cat len gia (truoc ${prevVal?.toFixed(5)} -> hien tai ${lastVal?.toFixed(5)})`
          : `${indLabel} chua cat len. Hien tai: ${lastVal?.toFixed(5)}, Gia: ${currentPrice.toFixed(5)}`,
      };
    }
    case 'crosses_below': {
      const crossed = detectCross(prices, values, 'crosses_below');
      return {
        satisfied: crossed,
        reason: crossed
          ? `${indLabel} cat xuong gia (truoc ${prevVal?.toFixed(5)} -> hien tai ${lastVal?.toFixed(5)})`
          : `${indLabel} chua cat xuong. Hien tai: ${lastVal?.toFixed(5)}, Gia: ${currentPrice.toFixed(5)}`,
      };
    }
    case 'greater_than': {
      if (lastVal === null) return { satisfied: false, reason: `${indLabel} chua tinh duoc` };
      if (rule.targetType === 'price') {
        const gt = currentPrice > lastVal;
        return {
          satisfied: gt,
          reason: gt
            ? `Gia ${currentPrice.toFixed(5)} > ${indLabel} ${lastVal.toFixed(5)}`
            : `Gia ${currentPrice.toFixed(5)} <= ${indLabel} ${lastVal.toFixed(5)}`,
        };
      }
      if (rule.targetType === 'level' && rule.targetValue === 'upper' && computed.upper) {
        const upperVal = lastValidValue(computed.upper);
        if (upperVal === null) return { satisfied: false, reason: 'BB Upper chua tinh duoc' };
        const gt = currentPrice > upperVal;
        return {
          satisfied: gt,
          reason: gt
            ? `Gia ${currentPrice.toFixed(5)} > BB Upper ${upperVal.toFixed(5)}`
            : `Gia ${currentPrice.toFixed(5)} <= BB Upper ${upperVal.toFixed(5)}`,
        };
      }
      if (rule.targetType === 'level' && rule.targetValue === 'middle' && computed.middle) {
        const midVal = lastValidValue(computed.middle);
        if (midVal === null) return { satisfied: false, reason: 'BB Middle chua tinh duoc' };
        const gt = currentPrice > midVal;
        return {
          satisfied: gt,
          reason: gt
            ? `Gia ${currentPrice.toFixed(5)} > BB Middle ${midVal.toFixed(5)}`
            : `Gia ${currentPrice.toFixed(5)} <= BB Middle ${midVal.toFixed(5)}`,
        };
      }
      if (rule.targetType === 'level' && rule.targetValue === 'lower' && computed.lower) {
        const lowVal = lastValidValue(computed.lower);
        if (lowVal === null) return { satisfied: false, reason: 'BB Lower chua tinh duoc' };
        const gt = currentPrice > lowVal;
        return {
          satisfied: gt,
          reason: gt
            ? `Gia ${currentPrice.toFixed(5)} > BB Lower ${lowVal.toFixed(5)}`
            : `Gia ${currentPrice.toFixed(5)} <= BB Lower ${lowVal.toFixed(5)}`,
        };
      }
      return { satisfied: false, reason: 'Target khong xac dinh' };
    }
    case 'less_than': {
      if (lastVal === null) return { satisfied: false, reason: `${indLabel} chua tinh duoc` };
      if (rule.targetType === 'price') {
        const lt = currentPrice < lastVal;
        return {
          satisfied: lt,
          reason: lt
            ? `Gia ${currentPrice.toFixed(5)} < ${indLabel} ${lastVal.toFixed(5)}`
            : `Gia ${currentPrice.toFixed(5)} >= ${indLabel} ${lastVal.toFixed(5)}`,
        };
      }
      if (rule.targetType === 'level' && rule.targetValue === 'lower' && computed.lower) {
        const lowerVal = lastValidValue(computed.lower);
        if (lowerVal === null) return { satisfied: false, reason: 'BB Lower chua tinh duoc' };
        const lt = currentPrice < lowerVal;
        return {
          satisfied: lt,
          reason: lt
            ? `Gia ${currentPrice.toFixed(5)} < BB Lower ${lowerVal.toFixed(5)}`
            : `Gia ${currentPrice.toFixed(5)} >= BB Lower ${lowerVal.toFixed(5)}`,
        };
      }
      if (rule.targetType === 'level' && rule.targetValue === 'upper' && computed.upper) {
        const upperVal = lastValidValue(computed.upper);
        if (upperVal === null) return { satisfied: false, reason: 'BB Upper chua tinh duoc' };
        const lt = currentPrice < upperVal;
        return {
          satisfied: lt,
          reason: lt
            ? `Gia ${currentPrice.toFixed(5)} < BB Upper ${upperVal.toFixed(5)}`
            : `Gia ${currentPrice.toFixed(5)} >= BB Upper ${upperVal.toFixed(5)}`,
        };
      }
      if (rule.targetType === 'level' && rule.targetValue === 'middle' && computed.middle) {
        const midVal = lastValidValue(computed.middle);
        if (midVal === null) return { satisfied: false, reason: 'BB Middle chua tinh duoc' };
        const lt = currentPrice < midVal;
        return {
          satisfied: lt,
          reason: lt
            ? `Gia ${currentPrice.toFixed(5)} < BB Middle ${midVal.toFixed(5)}`
            : `Gia ${currentPrice.toFixed(5)} >= BB Middle ${midVal.toFixed(5)}`,
        };
      }
      return { satisfied: false, reason: 'Target khong xac dinh' };
    }
    case 'equal_to': {
      if (lastVal === null) return { satisfied: false, reason: `${indLabel} chua tinh duoc` };
      const diffPips = Math.abs(currentPrice - lastVal) / pipSize;
      const eq = diffPips <= 3;
      return {
        satisfied: eq,
        reason: eq
          ? `Gia ${currentPrice.toFixed(5)} trong vong 3 pips cua ${indLabel} ${lastVal.toFixed(5)} (${diffPips.toFixed(1)} pips)`
          : `Gia ${currentPrice.toFixed(5)} xa ${indLabel} ${lastVal.toFixed(5)} (${diffPips.toFixed(1)} pips)`,
      };
    }
    default:
      return { satisfied: false, reason: `Dieu kien khong xac dinh: ${rule.condition}` };
  }
}

// ─── Main Signal Evaluator ─────────────────────────────────────

export function evaluateStrategySignal(
  candles: CandleData[],
  indicators: Indicator[],
  entryRules: EntryRule[],
  exitRules: ExitRule[],
  currentPrice: number,
  digits: number,
  stopLossPips: number,
  takeProfitPips: number,
): SignalResult {
  const pipSize = Math.pow(10, -digits + 1);
  const now = Date.now();

  // Evaluate entry rules
  const entryEvals: RuleEvaluation[] = entryRules.map((rule) => {
    const res = evaluateSingleRule(rule, candles, indicators, currentPrice, pipSize);
    return {
      ruleId: rule.id,
      description: `${rule.condition} ${rule.targetType}${rule.targetValue ? ' ' + rule.targetValue : ''}`,
      satisfied: res.satisfied,
      reason: res.reason,
      side: rule.side,
    };
  });

  // Evaluate exit rules
  const exitEvals: RuleEvaluation[] = exitRules.map((rule) => {
    const res = evaluateSingleRule(rule, candles, indicators, currentPrice, pipSize);
    return {
      ruleId: rule.id,
      description: `${rule.condition} ${rule.targetType}${rule.targetValue ? ' ' + rule.targetValue : ''}`,
      satisfied: res.satisfied,
      reason: res.reason,
      side: rule.side,
    };
  });

  // Group entry rules by side
  const buyRules = entryRules.filter((r) => (r as EntryRule & { side?: string }).side !== 'sell');
  const sellRules = entryRules.filter((r) => (r as EntryRule & { side?: string }).side === 'sell');

  const allBuyMet =
    buyRules.length > 0 &&
    buyRules.every((r) => entryEvals.find((e) => e.ruleId === r.id)?.satisfied);
  const allSellMet =
    sellRules.length > 0 &&
    sellRules.every((r) => entryEvals.find((e) => e.ruleId === r.id)?.satisfied);

  // Exit signals
  const exitBuyMet = exitEvals.some(
    (e) => e.satisfied && e.side !== 'sell',
  );
  const exitSellMet = exitEvals.some(
    (e) => e.satisfied && e.side === 'sell',
  );

  let direction: SignalDirection = 'NEUTRAL';
  let strength = 0;
  const entryReasons: string[] = [];
  const exitReasons: string[] = [];

  if (allBuyMet && !exitBuyMet) {
    direction = 'BUY';
    strength = 80;
    entryReasons.push(...entryEvals.filter((e) => e.satisfied && e.side !== 'sell').map((e) => e.reason));
  } else if (allSellMet && !exitSellMet) {
    direction = 'SELL';
    strength = 80;
    entryReasons.push(...entryEvals.filter((e) => e.satisfied && e.side === 'sell').map((e) => e.reason));
  }

  if (exitBuyMet) {
    exitReasons.push(...exitEvals.filter((e) => e.satisfied && e.side !== 'sell').map((e) => e.reason));
    if (direction === 'BUY') {
      direction = 'NEUTRAL';
      strength = 0;
    }
  }
  if (exitSellMet) {
    exitReasons.push(...exitEvals.filter((e) => e.satisfied && e.side === 'sell').map((e) => e.reason));
    if (direction === 'SELL') {
      direction = 'NEUTRAL';
      strength = 0;
    }
  }

  // If no signal but some rules are partially met, show distance info
  if (direction === 'NEUTRAL') {
    const nearBuy = entryEvals.filter((e) => !e.satisfied && e.side !== 'sell');
    const nearSell = entryEvals.filter((e) => !e.satisfied && e.side === 'sell');
    if (nearBuy.length > 0) {
      entryReasons.push(`Chua du dieu kien BUY: ${nearBuy.map((e) => e.reason).join('; ')}`);
    }
    if (nearSell.length > 0) {
      entryReasons.push(`Chua du dieu kien SELL: ${nearSell.map((e) => e.reason).join('; ')}`);
    }
  }

  // Build indicator snapshots
  const indicatorSnapshots: IndicatorSnapshot[] = indicators.map((ind) => {
    const computed = getIndicatorArray(candles, ind);
    const val = lastValidValue(computed.values);
    const prev = prevValidValue(computed.values);
    const dist = val !== null ? (currentPrice - val) / pipSize : null;
    return {
      label: `${ind.type}(${ind.period}${ind.type === 'BOLLINGER' ? `,${ind.deviation}` : ''})`,
      currentValue: val,
      previousValue: prev,
      distancePips: dist,
      isActive: val !== null,
    };
  });

  // Also add BB upper/lower/middle as separate snapshots if BOLLINGER present
  for (const ind of indicators) {
    if (ind.type === 'BOLLINGER') {
      const computed = getIndicatorArray(candles, ind);
      const upper = lastValidValue(computed.upper || []);
      const lower = lastValidValue(computed.lower || []);
      const middle = lastValidValue(computed.middle || []);

      if (upper !== null) {
        indicatorSnapshots.push({
          label: `BB Upper(${ind.period},${ind.deviation || 2})`,
          currentValue: upper,
          previousValue: prevValidValue(computed.upper || []),
          distancePips: (currentPrice - upper) / pipSize,
          isActive: true,
        });
      }
      if (lower !== null) {
        indicatorSnapshots.push({
          label: `BB Lower(${ind.period},${ind.deviation || 2})`,
          currentValue: lower,
          previousValue: prevValidValue(computed.lower || []),
          distancePips: (currentPrice - lower) / pipSize,
          isActive: true,
        });
      }
      if (middle !== null) {
        indicatorSnapshots.push({
          label: `BB Middle(${ind.period},${ind.deviation || 2})`,
          currentValue: middle,
          previousValue: prevValidValue(computed.middle || []),
          distancePips: (currentPrice - middle) / pipSize,
          isActive: true,
        });
      }
    }
  }

  // TP/SL projection (approximate for 1 lot = $10/pip on XAUUSD)
  const tpLevel = currentPrice + (direction === 'BUY' ? takeProfitPips * pipSize : direction === 'SELL' ? -takeProfitPips * pipSize : 0);
  const slLevel = currentPrice + (direction === 'BUY' ? -stopLossPips * pipSize : direction === 'SELL' ? stopLossPips * pipSize : 0);

  return {
    direction,
    strength,
    entryReasons,
    exitReasons,
    indicators: indicatorSnapshots,
    currentPrice,
    pipSize,
    timestamp: now,
    tpLevel,
    slLevel,
  };
}

// ─── Lot Size Calculator ─────────────────────────────────────────

export function calculateLotSize(
  balance: number,
  riskPercent: number,
  stopLossPips: number,
  pipValuePerLot = 10, // Default XAUUSD ~$10/pip per lot
): number {
  if (balance <= 0 || riskPercent <= 0 || stopLossPips <= 0) return 0;
  const riskAmount = balance * (riskPercent / 100);
  const riskPerLot = stopLossPips * pipValuePerLot;
  if (riskPerLot <= 0) return 0;
  const lots = riskAmount / riskPerLot;
  // Round to 2 decimal places
  return Math.floor(lots * 100) / 100;
}